import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  ArrowLeft, Loader2, Trophy, FileText, ImageIcon, Share2, Send, Copy,
  ZoomIn, ZoomOut, RotateCcw, Upload,
} from "lucide-react";
import CompetitionPoster from "@/components/announcement/CompetitionPoster";
import { exportImage, exportPdf, exportDataUrl, shareNode, copyNodeImage } from "@/components/announcement/exportUtils";
import TemplateBar from "@/components/announcement/TemplateBar";
import { SPORTS_ART_PACK } from "@/components/announcement/sportsArtPack";

const MONTHS = ["JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE",
                "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"];

const PRESETS = [
  {
    name: "Kannada Handwriting",
    line1: "KANNADA", line2: "HANDWRITING", script: "Competition",
    tagline: "Good Handwriting is a Reflection of a Disciplined Mind and a Creative Soul.",
    bottom_bar: "LET YOUR HANDWRITING SPEAK YOUR EXCELLENCE!",
    badge: "SHOW YOUR BEST HANDWRITING AND WIN APPRECIATION!",
    book_labels: "Grammar, Vocabulary, Comprehension",
  },
  {
    name: "English Elocution",
    line1: "ENGLISH", line2: "ELOCUTION", script: "Competition",
    tagline: "Words have the power to inspire, influence and ignite young minds.",
    bottom_bar: "LET YOUR VOICE MAKE A DIFFERENCE!",
    badge: "SPEAK BOLDLY AND WIN APPRECIATION!",
    book_labels: "Diction, Poise, Confidence",
  },
  {
    name: "Drawing & Painting",
    line1: "DRAWING", line2: "& PAINTING", script: "Competition",
    tagline: "Every artist was first an amateur — let your colours tell a story.",
    bottom_bar: "LET YOUR COLOURS SPEAK YOUR IMAGINATION!",
    badge: "SHOW YOUR BEST ARTWORK AND WIN!",
    book_labels: "Sketch, Colour, Creativity",
  },
  {
    name: "Quiz",
    line1: "GENERAL", line2: "KNOWLEDGE", script: "Quiz",
    tagline: "A curious mind is the beginning of wisdom.",
    bottom_bar: "LET YOUR CURIOSITY LEAD THE WAY!",
    badge: "ANSWER FAST AND WIN APPRECIATION!",
    book_labels: "Reason, Recall, Reflect",
  },
  {
    name: "Sports Meet",
    line1: "ANNUAL", line2: "SPORTS MEET", script: "Championship",
    tagline: "Champions keep playing until they get it right.",
    bottom_bar: "LET YOUR SPIRIT SHINE ON THE FIELD!",
    badge: "PLAY HARD AND WIN GLORY!",
    book_labels: "Team, Grit, Victory",
    art: SPORTS_ART_PACK,   // trophy + medal + torch inline SVGs
  },
];

export default function CompetitionGenerator({ onBack }) {
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);

  const first = PRESETS[0];
  const [line1, setLine1] = useState(first.line1);
  const [line2, setLine2] = useState(first.line2);
  const [script, setScript] = useState(first.script);
  const [tagline, setTagline] = useState(first.tagline);
  const [monthLabel, setMonthLabel] = useState(() => `${MONTHS[new Date().getMonth()]} MONTH WINNERS`);
  const [congrats, setCongrats] = useState("Congratulations to All Winners!");
  const [bottomBar, setBottomBar] = useState(first.bottom_bar);
  const [badge, setBadge] = useState(first.badge);
  const [footerLine, setFooterLine] = useState("All the Students are Cordially Invited");
  const [bookLabels, setBookLabels] = useState(first.book_labels);
  const [photoDataUrl, setPhotoDataUrl] = useState("");
  const [photo, setPhoto] = useState({ scale: 1, x: 0, y: 0, override: null });
  // Optional custom decoration overrides (data URLs).
  const [artInk, setArtInk] = useState("");
  const [artPen, setArtPen] = useState("");
  const [artBooks, setArtBooks] = useState("");
  const [autoCutBg, setAutoCutBg] = useState(true);

  const [audienceScope, setAudienceScope] = useState("school");
  const [exporting, setExporting] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const posterRef = useRef(null);
  const previewWrapRef = useRef(null);
  const fileRef = useRef(null);
  const inkFileRef = useRef(null);
  const penFileRef = useRef(null);
  const booksFileRef = useRef(null);
  const dragRef = useRef(null);
  const [pscale, setPscale] = useState(1);

  useEffect(() => {
    setLoading(true);
    api.get("/school/profile")
      .then(({ data }) => { setProfile((data && data.profile) || {}); })
      .catch(() => toast.error("Could not load school profile."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const el = previewWrapRef.current;
    if (!el) return;
    const update = () => setPscale(Math.min(1, (el.clientWidth - 4) / 794));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const bookLabelsArr = useMemo(
    () => (bookLabels || "").split(",").map((s) => s.trim()).filter(Boolean).slice(0, 3),
    [bookLabels]
  );

  const applyPreset = (p) => {
    setLine1(p.line1); setLine2(p.line2); setScript(p.script);
    setTagline(p.tagline); setBottomBar(p.bottom_bar); setBadge(p.badge);
    setBookLabels(p.book_labels);
    // Presets with a bundled art pack (e.g. Sports Meet) swap all three
    // decorations at once. Everything else clears back to the bundled defaults.
    if (p.art) {
      setArtInk(p.art.art_ink || "");
      setArtPen(p.art.art_pen || "");
      setArtBooks(p.art.art_books || "");
    } else {
      setArtInk(""); setArtPen(""); setArtBooks("");
    }
  };

  const onUploadPhoto = (file) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = () => {
      setPhoto((p) => ({ ...p, override: r.result, scale: 1, x: 0, y: 0 }));
      setPhotoDataUrl(r.result);
    };
    r.readAsDataURL(file);
  };

  // ── Custom decoration uploads ──
  // Loads the file, optionally flood-fills near-white/cream edges to transparency
  // so the artwork sits directly on the cream A4 sheet with no visible sticker box.
  const stripBackgroundIfNeeded = (dataUrl) => new Promise((resolve) => {
    if (!autoCutBg) { resolve(dataUrl); return; }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth; c.height = img.naturalHeight;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const id = ctx.getImageData(0, 0, c.width, c.height);
        const d = id.data;
        const W = c.width, H = c.height;
        const TOL = 46;   // colour distance from cream/white
        const CR = 250, CG = 247, CB = 242;
        const near = new Uint8Array(W * H);
        for (let i = 0, p = 0; i < d.length; i += 4, p++) {
          const dr = d[i] - CR, dg = d[i + 1] - CG, db = d[i + 2] - CB;
          if (Math.sqrt(dr * dr + dg * dg + db * db) < TOL || (d[i] > 240 && d[i + 1] > 240 && d[i + 2] > 240)) {
            near[p] = 1;
          }
        }
        // BFS flood from every edge pixel that is "near cream/white".
        const keep = new Uint8Array(W * H);
        const stack = [];
        const push = (y, x) => { const p = y * W + x; if (near[p] && !keep[p]) { keep[p] = 1; stack.push(p); } };
        for (let x = 0; x < W; x++) { push(0, x); push(H - 1, x); }
        for (let y = 0; y < H; y++) { push(y, 0); push(y, W - 1); }
        while (stack.length) {
          const p = stack.pop();
          const y = (p / W) | 0, x = p % W;
          if (y > 0)      { const q = p - W; if (near[q] && !keep[q]) { keep[q] = 1; stack.push(q); } }
          if (y < H - 1)  { const q = p + W; if (near[q] && !keep[q]) { keep[q] = 1; stack.push(q); } }
          if (x > 0)      { const q = p - 1; if (near[q] && !keep[q]) { keep[q] = 1; stack.push(q); } }
          if (x < W - 1)  { const q = p + 1; if (near[q] && !keep[q]) { keep[q] = 1; stack.push(q); } }
        }
        for (let p = 0, i = 0; p < W * H; p++, i += 4) { if (keep[p]) d[i + 3] = 0; }
        ctx.putImageData(id, 0, 0);
        resolve(c.toDataURL("image/png"));
      } catch { resolve(dataUrl); }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });

  const onUploadArt = (slot) => async (file) => {
    if (!file) return;
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    const clean = await stripBackgroundIfNeeded(dataUrl);
    if (slot === "ink") setArtInk(clean);
    else if (slot === "pen") setArtPen(clean);
    else if (slot === "books") setArtBooks(clean);
    toast.success(`${slot === "ink" ? "Ink pot" : slot === "pen" ? "Hand & pen" : "Books"} artwork updated.`);
  };

  const zoom = (d) => setPhoto((p) => ({ ...p, scale: Math.min(3, Math.max(1, +(p.scale + d).toFixed(2))) }));
  const resetPhoto = () => setPhoto((p) => ({ ...p, scale: 1, x: 0, y: 0 }));
  const onPointerDown = (e) => {
    if (!photoDataUrl && !photo.override) return;
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: photo.x, oy: photo.y };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const k = pscale || 1;
    setPhoto((p) => ({ ...p, x: dragRef.current.ox + (e.clientX - dragRef.current.sx) / k, y: dragRef.current.oy + (e.clientY - dragRef.current.sy) / k }));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const posterData = {
    profile,
    line1, line2, script, tagline,
    month_label: monthLabel,
    congrats,
    bottom_bar: bottomBar,
    badge,
    footer_line: footerLine,
    photo_url: photoDataUrl,
    book_labels: bookLabelsArr.length ? bookLabelsArr : ["Grammar", "Vocabulary", "Comprehension"],
    art_ink: artInk,
    art_pen: artPen,
    art_books: artBooks,
  };

  const title = `Competition-${(line1 || "").replace(/[^\w-]+/g, "_")}-${(line2 || "").replace(/[^\w-]+/g, "_")}`;

  const doExport = async (kind) => {
    if (!posterRef.current) return;
    setExporting(true);
    try {
      if (kind === "pdf") await exportPdf(posterRef.current, title, "portrait");
      else await exportImage(posterRef.current, title, kind);
    } catch { toast.error("Export failed. Please try again."); }
    finally { setExporting(false); }
  };
  const doShare = async () => {
    if (!posterRef.current) return;
    setExporting(true);
    try {
      const res = await shareNode(posterRef.current, title);
      if (res === "unsupported") { await doExport("png"); toast.message("Sharing isn't available here — image downloaded instead."); }
    } catch (e) { if (e?.name !== "AbortError") toast.error("Could not share."); }
    finally { setExporting(false); }
  };
  const doCopy = async () => {
    if (!posterRef.current) return;
    setExporting(true);
    try {
      const res = await copyNodeImage(posterRef.current);
      if (res === "copied") toast.success("Poster copied to clipboard.");
      else { await doExport("png"); toast.message("Clipboard unavailable — downloaded the image instead."); }
    } catch { toast.error("Could not copy image."); }
    finally { setExporting(false); }
  };

  const doPublish = async () => {
    if (!posterRef.current) return;
    setPublishing(true);
    try {
      const image = await exportDataUrl(posterRef.current, "jpeg", 2.5);
      const audience = audienceScope === "school"
        ? { type: "school", recipients: ["students", "parents", "teachers"] }
        : { type: "all_students", recipients: ["students", "parents"] };
      await api.post("/notices?publish=true", {
        title: `${line1} ${line2} ${script} — ${monthLabel}`,
        category: "Competition",
        description: `<p>${tagline}</p><p><b>${congrats}</b></p><p>${footerLine}</p>`,
        priority: "normal",
        image,
        announcement_date: new Date().toISOString().slice(0, 10),
        audience,
      });
      toast.success("Competition announcement published!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not publish. Ensure the school profile is complete.");
    } finally { setPublishing(false); }
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-slate-400" data-testid="competition-loading">
        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="competition-generator">
      <div className="flex items-center gap-3 flex-wrap">
        {onBack && (
          <Button variant="ghost" size="sm" className="rounded-full" onClick={onBack} data-testid="competition-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        )}
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2 text-lg font-semibold">
            <Trophy className="h-5 w-5 text-amber-500" /> Competition Poster
          </div>
          <p className="text-sm text-slate-500">Design an editable competition / winners poster and publish it as an announcement.</p>
        </div>
        <TemplateBar
          kind="competition"
          testIdPrefix="comp"
          getState={() => ({
            line1, line2, script, tagline,
            monthLabel, congrats, bottomBar, badge, footerLine, bookLabels,
            artInk, artPen, artBooks,
          })}
          applyState={(s) => {
            if (!s || typeof s !== "object") return;
            if (typeof s.line1 === "string") setLine1(s.line1);
            if (typeof s.line2 === "string") setLine2(s.line2);
            if (typeof s.script === "string") setScript(s.script);
            if (typeof s.tagline === "string") setTagline(s.tagline);
            if (typeof s.monthLabel === "string") setMonthLabel(s.monthLabel);
            if (typeof s.congrats === "string") setCongrats(s.congrats);
            if (typeof s.bottomBar === "string") setBottomBar(s.bottomBar);
            if (typeof s.badge === "string") setBadge(s.badge);
            if (typeof s.footerLine === "string") setFooterLine(s.footerLine);
            if (typeof s.bookLabels === "string") setBookLabels(s.bookLabels);
            setArtInk(typeof s.artInk === "string" ? s.artInk : "");
            setArtPen(typeof s.artPen === "string" ? s.artPen : "");
            setArtBooks(typeof s.artBooks === "string" ? s.artBooks : "");
          }}
        />
      </div>

      <div className="grid lg:grid-cols-[420px_1fr] gap-5">
        {/* ── Editor ── */}
        <div className="space-y-3">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-900 space-y-3">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Quick templates</Label>
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map((p) => (
                <button key={p.name} onClick={() => applyPreset(p)}
                  className="text-[11px] px-2.5 py-1 rounded-full border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
                  data-testid={`comp-preset-${p.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>{p.name}</button>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-900 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Title Line 1</Label>
                <Input value={line1} onChange={(e) => setLine1(e.target.value.toUpperCase())} className="rounded-xl mt-1" data-testid="comp-line1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Title Line 2</Label>
                <Input value={line2} onChange={(e) => setLine2(e.target.value.toUpperCase())} className="rounded-xl mt-1" data-testid="comp-line2" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Script accent (cursive)</Label>
              <Input value={script} onChange={(e) => setScript(e.target.value)} className="rounded-xl mt-1" data-testid="comp-script" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Tagline (yellow ribbon)</Label>
              <Textarea value={tagline} onChange={(e) => setTagline(e.target.value)} rows={2} className="rounded-xl mt-1" data-testid="comp-tagline" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Month / winners label</Label>
              <Input value={monthLabel} onChange={(e) => setMonthLabel(e.target.value.toUpperCase())} className="rounded-xl mt-1" data-testid="comp-month" />
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {MONTHS.map((m) => (
                  <button key={m} onClick={() => setMonthLabel(`${m} MONTH WINNERS`)} className="text-[11px] px-2 py-0.5 rounded-full border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800" data-testid={`comp-month-${m.toLowerCase()}`}>{m.slice(0, 3)}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Photo */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-900 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Winners photo</Label>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} data-testid="comp-photo-btn">
                <Upload className="h-4 w-4 mr-1" /> Upload
              </Button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onUploadPhoto(e.target.files?.[0])} data-testid="comp-photo-input" />
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => zoom(0.1)} disabled={!photoDataUrl && !photo.override} data-testid="comp-zoom-in"><ZoomIn className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => zoom(-0.1)} disabled={!photoDataUrl && !photo.override} data-testid="comp-zoom-out"><ZoomOut className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={resetPhoto} disabled={!photoDataUrl && !photo.override} data-testid="comp-photo-reset"><RotateCcw className="h-4 w-4" /></Button>
              <span className="text-xs text-slate-500 ml-1">{photo.scale.toFixed(2)}× · drag preview to reposition</span>
            </div>
          </div>

          {/* Custom decoration artwork */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-900 space-y-3" data-testid="comp-art-block">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Custom decoration artwork</Label>
              <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer">
                <input type="checkbox" checked={autoCutBg} onChange={(e) => setAutoCutBg(e.target.checked)} data-testid="comp-art-autocut" />
                Auto-remove white/cream background
              </label>
            </div>
            {[
              { key: "ink",   label: "Ink pot (mid-left)",       src: artInk,   set: setArtInk,   ref: inkFileRef,   fallback: "/competition/ink.png" },
              { key: "pen",   label: "Hand with pen (top-right)", src: artPen,   set: setArtPen,   ref: penFileRef,   fallback: "/competition/hand-pen.png" },
              { key: "books", label: "Books stack (bottom-left)", src: artBooks, set: setArtBooks, ref: booksFileRef, fallback: "/competition/books.png" },
            ].map(({ key, label, src, set, ref, fallback }) => (
              <div key={key} className="flex items-center gap-3" data-testid={`comp-art-row-${key}`}>
                <div className="h-14 w-14 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex items-center justify-center overflow-hidden shrink-0">
                  <img src={src || fallback} alt="" className="max-h-14 max-w-14 object-contain" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{label}</div>
                  <div className="text-[11px] text-slate-500">{src ? "Custom uploaded" : "Default (from sample)"}</div>
                </div>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => ref.current?.click()} data-testid={`comp-art-upload-${key}`}>
                  <Upload className="h-3.5 w-3.5 mr-1" /> Upload
                </Button>
                {src ? (
                  <Button size="sm" variant="ghost" className="rounded-full text-rose-500 hover:text-rose-600" onClick={() => set("")} data-testid={`comp-art-reset-${key}`}>
                    <RotateCcw className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
                <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => onUploadArt(key)(e.target.files?.[0])} data-testid={`comp-art-input-${key}`} />
              </div>
            ))}
            <p className="text-[11px] text-slate-500 leading-relaxed">
              Tip: use a PNG or JPEG with the subject centred. The auto-cutter removes the pale rectangular background so the artwork sits flush on the cream sheet.
            </p>
          </div>

          {/* Text extras */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-900 space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Congratulations ribbon</Label>
              <Input value={congrats} onChange={(e) => setCongrats(e.target.value)} className="rounded-xl mt-1" data-testid="comp-congrats" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Blue bar tagline</Label>
              <Input value={bottomBar} onChange={(e) => setBottomBar(e.target.value.toUpperCase())} className="rounded-xl mt-1" data-testid="comp-bottom-bar" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Red badge (3-line)</Label>
              <Textarea value={badge} onChange={(e) => setBadge(e.target.value)} rows={2} className="rounded-xl mt-1" data-testid="comp-badge" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Footer cursive line</Label>
              <Input value={footerLine} onChange={(e) => setFooterLine(e.target.value)} className="rounded-xl mt-1" data-testid="comp-footer" />
            </div>
          </div>

          {/* Actions */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-900 space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Publish to</Label>
              <Select value={audienceScope} onValueChange={setAudienceScope}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="comp-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="school">Entire school</SelectItem>
                  <SelectItem value="students">All students only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => doExport("pdf")} disabled={exporting} data-testid="comp-download-pdf">
                {exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />} PDF
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => doExport("png")} disabled={exporting} data-testid="comp-download-png">
                <ImageIcon className="h-4 w-4 mr-1" /> PNG
              </Button>
              <Button variant="outline" className="rounded-full" onClick={doCopy} disabled={exporting} data-testid="comp-copy">
                <Copy className="h-4 w-4 mr-1" /> Copy image
              </Button>
              <Button variant="outline" className="rounded-full" onClick={doShare} disabled={exporting} data-testid="comp-share">
                <Share2 className="h-4 w-4 mr-1" /> Share
              </Button>
            </div>
            <Button className="rounded-full w-full" onClick={doPublish} disabled={publishing} data-testid="comp-publish">
              {publishing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
              Publish as Announcement
            </Button>
          </div>
        </div>

        {/* ── Preview ── */}
        <div>
          <div
            ref={previewWrapRef}
            className="w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/40 p-3 overflow-hidden"
            data-testid="comp-preview-wrap"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
          >
            <div style={{ height: 1123 * pscale, width: "100%", position: "relative" }}>
              <div style={{ width: 794, height: 1123, transform: `scale(${pscale})`, transformOrigin: "top left" }}>
                <CompetitionPoster ref={posterRef} data={posterData} photo={photo} />
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2">Live preview scales to fit — the download stays crisp at full A4 portrait.</p>
        </div>
      </div>
    </div>
  );
}
